import axios from "axios";
import { ApiSuccess, saUrl } from "./api_helper";

/** Duração do desconto SaaS (DOC 02 § 4.1) — espelha CouponDurationType na API. */
export type CouponDurationType =
  | "first_invoice"
  | "limited_cycles"
  | "lifetime";

export type SaCoupon = {
  id: number;
  code: string;
  discount_percent_bps: number;
  duration_type: CouponDurationType | string;
  duration_cycles: number | null;
  max_redemptions: number | null;
  redemption_count: number;
  valid_from: string | null;
  valid_until: string | null;
  applicable_plan_ids: number[] | null;
  applicable_currencies: string[] | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
};

/** Payload create — bps já convertidos no form (UI mostra %). */
export type SaCouponCreatePayload = {
  code: string;
  discount_percent_bps: number;
  duration_type: CouponDurationType;
  duration_cycles?: number | null;
  max_redemptions?: number | null;
  valid_from?: string | null;
  valid_until?: string | null;
  applicable_plan_ids?: number[] | null;
  applicable_currencies?: string[] | null;
  is_active?: boolean;
};

export type SaCouponUpdatePayload = Partial<SaCouponCreatePayload>;

export type SaCouponApplication = {
  id: number;
  tenant_id: number;
  /** Nested para link da ficha do cliente (LAC-082). */
  tenant?: { id: number; name: string } | null;
  subscription_id: number;
  code: string;
  amount_cents: number;
  status: string;
  cycles_remaining?: number | null;
  applied_at: string | null;
};

export type SaCouponApplicationsListParams = {
  page?: number;
  per_page?: number;
  /** LAC-091: filtro por tenant específico. */
  tenant_id?: number;
  /** LAC-091: busca por nome do tenant (LIKE). */
  search?: string;
  /** LAC-091: status = active | completed | canceled. */
  status?: "active" | "completed" | "canceled" | "";
};

export type PaginatedMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type CouponsListResponse = ApiSuccess<SaCoupon[]> & {
  meta: PaginatedMeta;
  links?: unknown;
};

export type SaCouponsListParams = {
  search?: string;
  is_active?: boolean;
  currency?: string;
  page?: number;
  per_page?: number;
};

/** UI % → API bps (20 → 2000). */
export const percentToBps = (percent: number): number =>
  Math.round(percent * 100);

/** API bps → UI % (2000 → 20). */
export const bpsToPercent = (bps: number): number => bps / 100;

/**
 * GET /super-admin/coupons — URL absoluta (fora de /api/v1).
 * Token SA via interceptor (sa_authUser).
 */
export const getSuperAdminCoupons = (
  params?: SaCouponsListParams
): Promise<CouponsListResponse> => {
  const query: Record<string, string | number | boolean> = {
    per_page: params?.per_page || 50,
  };

  if (params?.page) {
    query.page = params.page;
  }
  if (params?.search) {
    query.search = params.search;
  }
  if (params?.currency) {
    query.currency = params.currency;
  }
  if (typeof params?.is_active === "boolean") {
    query.is_active = params.is_active ? 1 : 0;
  }

  return axios.get(saUrl("/super-admin/coupons"), { params: query });
};

export const getSuperAdminCoupon = (
  id: number
): Promise<ApiSuccess<SaCoupon>> => {
  return axios.get(saUrl(`/super-admin/coupons/${id}`));
};

export const createSuperAdminCoupon = (
  payload: SaCouponCreatePayload
): Promise<ApiSuccess<SaCoupon>> => {
  return axios.post(saUrl("/super-admin/coupons"), payload);
};

export const updateSuperAdminCoupon = (
  id: number,
  payload: SaCouponUpdatePayload
): Promise<ApiSuccess<SaCoupon>> => {
  return axios.patch(saUrl(`/super-admin/coupons/${id}`), payload);
};

type CouponApplicationsListResponse = ApiSuccess<SaCouponApplication[]> & {
  meta: PaginatedMeta;
  links?: unknown;
};

/** GET resgates de um cupom — paginado (SA only). LAC-091: filtros tenant_id/search/status. */
export const getSuperAdminCouponApplications = (
  couponId: number,
  params?: SaCouponApplicationsListParams
): Promise<CouponApplicationsListResponse> => {
  const query: Record<string, string | number> = {
    per_page: params?.per_page || 50,
  };
  if (params?.page) {
    query.page = params.page;
  }
  if (params?.tenant_id) {
    query.tenant_id = params.tenant_id;
  }
  if (params?.search) {
    query.search = params.search;
  }
  if (params?.status) {
    query.status = params.status;
  }
  return axios.get(saUrl(`/super-admin/coupons/${couponId}/applications`), {
    params: query,
  });
};

/**
 * GET CSV de resgates — blob (não JSON). Dispara download no browser.
 * O interceptor devolve `response.data` (o Blob); Content-Disposition não chega aqui,
 * então usamos `fallbackFilename` (código do cupom na UI).
 * LAC-091: suporta mesmos filtros de listagem (tenant_id, search, status).
 */
export const downloadSuperAdminCouponApplicationsCsv = async (
  couponId: number,
  fallbackFilename?: string,
  filters?: Pick<SaCouponApplicationsListParams, "tenant_id" | "search" | "status">
): Promise<void> => {
  const params: Record<string, string | number> = {};
  if (filters?.tenant_id) {
    params.tenant_id = filters.tenant_id;
  }
  if (filters?.search) {
    params.search = filters.search;
  }
  if (filters?.status) {
    params.status = filters.status;
  }

  const data = await axios.get(
    saUrl(`/super-admin/coupons/${couponId}/applications/export`),
    { responseType: "blob", params }
  );

  // Interceptor devolve response.data; tipagem Axios vs BlobPart exige cast via unknown.
  const blob =
    data instanceof Blob
      ? data
      : new Blob([data as unknown as BlobPart], {
          type: "text/csv;charset=utf-8",
        });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download =
    fallbackFilename || `coupon-${couponId}-applications.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};
